import { useImperativeHandle, useRef, type Ref, type RefObject } from 'react';

import { useMergeRefs } from '../../../core/hooks/useMergeRefs.js';
import { useDataTableV2ConfigElement } from '../../hooks/useDataTableV2Config.js';
import { DataTableV2ColumnSettingsModalRef } from '../components/Toolbar/ColumnSettings/DataTableV2ColumnSettingsModal.js';
import type { Table } from '../hooks/useTable/types.js';
import {
  type DataTableV2ColumnSettingsConfig,
  type DataTableV2Ref,
} from '../public.api.js';
import type { DataTableV2RowThreshold } from './Thresholds/threshold-types.js';
import type { DataTableV2ThresholdProps } from './Thresholds/Thresholds.js';

/**
 * Extends the ref of the DataTableV2 with additional configuration and control options.
 * @internal
 */
export function useDataTableV2ConfigRef<TData>(
  forwardedRef: Ref<DataTableV2Ref>,
  columnSettingsModalRef: RefObject<DataTableV2ColumnSettingsModalRef | null>,
  table: Table<TData>,
  props: DataTableV2ThresholdProps<TData>,
) {
  /*
   * Collect the refs and create an imperative handle in order to expose the
   * functions we need on the DataTableV2.
   */
  const containerRef = useRef<HTMLDivElement>(null);
  const rootRef = useMergeRefs<DataTableV2Ref | HTMLDivElement>([
    forwardedRef,
    containerRef,
  ]);

  const configRowThresholds = useDataTableV2ConfigElement('rowThresholds');

  useImperativeHandle(forwardedRef, () => ({
    element: containerRef.current,
    // eslint-disable-next-line @typescript-eslint/prefer-ts-expect-error, @typescript-eslint/ban-ts-comment
    // @ts-ignore
    getConfig() {
      return {
        pagination: {
          defaultPageIndex: table.options.state.pagination?.pageIndex,
          defaultPageSize: table.options.state.pagination?.pageSize,
        },
        columnVisibility: table.options.state.columnVisibility,
        fullWidth: table.options.layout?.fullWidth,
        fullHeight: table.options.layout?.fullHeight,
        lineWrap: table.options.state.columnLineWrap,
        resizable: table.options.enableColumnResizing,
        selectableRows: table.options.enableRowSelection,
        // NOTE: typecast necessary due to generic resolving to unknown
        rowThresholds: (props.rowThresholds ?? configRowThresholds) as
          | DataTableV2RowThreshold<unknown>[]
          | undefined,
        sortable: table.options.enableSorting,
        variant: table.options.state.visualVariantConfig,
      };
    },
    downloadData(subset: 'all' | 'page' | 'selected') {
      switch (subset) {
        case 'all':
          table.downloadAllData();
          break;
        case 'page':
          table.downloadPageData();
          break;
        case 'selected':
          table.downloadSelectedData();
          break;
      }
    },
    scrollToRow: table.scrollToRow,
    openColumnSettings: (columnSettings?: DataTableV2ColumnSettingsConfig) => {
      columnSettingsModalRef.current?.openColumnSettings(columnSettings);
    },
  }));

  return rootRef;
}
